import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTabParam } from '../../../common/hooks/use-tab-param.hook';
import { useTranslation } from 'react-i18next';
import { BookOpen, ChevronDown, CircleHelp, Headset, ImagePlus, ListChecks, MousePointerClick, Palette, Plug, Repeat, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

import { TopNavBar } from '../../../common/Header/TopNavBar';
import { SettingsColorField, SettingsGroup, SettingsHint, SettingsLabel } from '../../../components/design-system/common/SettingsPanel';
import { StatusBadge } from '../../../components/design-system/admin/StatusBadge';
import { FeatureGate } from '../../../components/FeatureGate';
import { useSettingsApi } from '../../../common/hooks/use-settings-api.hook';
import { useStoreKnowledgeApi } from '../../../common/hooks/use-store-knowledge-api.hook';
import { StoreKnowledgePanel } from '../../../modules/store-knowledge/StoreKnowledgePanel';
import { SHELL_HEIGHT } from '../../../modules/vendor/assistant/askai-layout';
import type { ChatSettings, FeatureBlock, IntegrationConfig } from '../../../modules/vendor/settings/models/config.model';
import { applyChatPatch, prepareChatSave, resolveLauncherColors } from '../../../modules/vendor/settings/models/chat-settings';
import { ChatPreview, PreviewView } from './chat-preview';
import { ReturningConsentField } from './returning-consent-field';
import { ReturningRuleField } from './returning-rule-field';
import { AssistantRulesField } from './assistant-rules-field';
import { AutosaveReporterContext, useAutosave, useAutosaveReporterState } from '../../../common/hooks/use-autosave.hook';
import { AutosaveIndicator } from '../../../components/design-system/common/AutosaveIndicator';

/**
 * /assistant/settings?tab=questions
 *
 * The page used to be four tabs; it is one column now, most important first,
 * with the rest folded. The tab names stay, because the sidebar and other pages
 * link to them: `?tab=` says which folded section is open.
 */
const CHAT_SETTINGS_TABS = ['general', 'appearance', 'questions', 'photos', 'team', 'returning'] as const;
type ChatSettingsTab = (typeof CHAT_SETTINGS_TABS)[number];

const DEFAULT_SETTINGS: ChatSettings = {
    position: 'right',
    greeting: '',
    label: '',
    color: '',
    textColor: '',
    hoverColor: '',
    hoverTextColor: '',
};

const FieldLabel = ({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) => (
    <SettingsLabel htmlFor={htmlFor} className="mb-1.5 block">{children}</SettingsLabel>
);

/**
 * A folded group of settings: its name, one line on what it does, whether it
 * is on — and, opened, its fields. Only one is open at a time, and which one
 * is in the URL.
 */
const Disclosure = ({
    id,
    icon,
    title,
    description,
    status,
    open,
    onOpenChange,
    children,
}: {
    id: string;
    icon: ReactNode;
    title: ReactNode;
    description: ReactNode;
    status?: ReactNode;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    children: ReactNode;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    // Opened from a link (?tab=photos): bring it into view once.
    useEffect(() => {
        if (open) ref.current?.scrollIntoView?.({ block: 'nearest' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <Collapsible open={open} onOpenChange={onOpenChange} className="rounded-xl border" data-testid={`chat-section-${id}`}>
            <div ref={ref}>
                <CollapsibleTrigger className="group/section flex w-full items-start gap-3 px-4 py-3.5 text-start">
                    <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md border bg-muted/50 text-muted-foreground [&_svg]:size-3.5">
                        {icon}
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-3">
                            <span className="text-sm font-medium">{title}</span>
                            {status}
                        </span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">{description}</span>
                    </span>
                    <ChevronDown className="mt-1 size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/section:rotate-180" />
                </CollapsibleTrigger>
            </div>
            <CollapsibleContent>
                <div className="flex flex-col gap-5 border-t px-4 py-4">{children}</div>
            </CollapsibleContent>
        </Collapsible>
    );
};

/** On or off, as a dot and a word — green only when it is live for shoppers. */
const OnOff = ({ on }: { on: boolean }) => {
    const { t } = useTranslation();
    return <StatusBadge tone={on ? 'success' : 'neutral'} className="text-muted-foreground">{on ? t('SHOPPER_UI.SETTINGS.ON') : t('SHOPPER_UI.SETTINGS.OFF')}</StatusBadge>;
};

/**
 * Shopper chat configuration, as an editor: settings on the left, a live
 * mock-up on a canvas to the right.
 *
 * The chat's settings live in `integration.features.chat`, the same map the
 * Integration page writes its other features into — so this page loads the
 * whole integration object, patches only the chat block, and saves it back.
 * Patching rather than replacing matters: a vendor can have this page and the
 * Integration page open at once, and a save from here must not drop the domains
 * or matching rules configured over there.
 *
 * The column reads top to bottom in the order a vendor decides things: is it
 * on, what does it say first, what does it know — then, folded, the room
 * photos, the questions block and the button's look.
 */
const ChatSettingsBody = () => {
    const { t } = useTranslation();
    // /assistant/settings?tab=questions — which folded section is open survives a reload and can be linked to.
    const [settingsTab, setSettingsTab] = useTabParam(CHAT_SETTINGS_TABS);
    const navigate = useNavigate();
    const settingsAPI = useSettingsApi();
    const storeKnowledgeApi = useStoreKnowledgeApi();

    const [integration, setIntegration] = useState<IntegrationConfig | null>(null);
    /** The vendor's brand colour — what the launcher uses when no colour is set. */
    const [brandColor, setBrandColor] = useState<string | undefined>(undefined);
    /** The brand's secondary colour — the launcher's hover background by default. */
    const [brandSecondaryColor, setBrandSecondaryColor] = useState<string | undefined>(undefined);
    const [company, setCompany] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState(true);

    const [previewView, setPreviewView] = useState<PreviewView>(
        settingsTab === 'questions' ? 'page' : settingsTab === 'returning' ? 'returning' : 'chat',
    );
    const [previewHover, setPreviewHover] = useState(false);

    const chat: FeatureBlock | undefined = integration?.features?.chat;
    const enabled = !!chat?.enabled;
    const settings: ChatSettings = { ...DEFAULT_SETTINGS, ...((chat?.settings as ChatSettings) || {}) };
    const launcherColors = resolveLauncherColors(settings, brandColor, brandSecondaryColor);

    /*
     * The chat needs the SDK on the vendor's site and at least one authorised
     * domain, exactly like every other feature. Saying so here — with a way to
     * get there — beats a vendor turning the chat on and finding nothing
     * appears on their shop.
     */
    const installed = !!integration?.allowedDomains?.length;

    const load = useCallback(async () => {
        try {
            const config = await settingsAPI.findConfig();
            const loaded = (config?.integration || null) as IntegrationConfig | null;
            setIntegration(loaded);
            setBrandColor(config?.primaryColor || undefined);
            setBrandSecondaryColor(config?.secondaryColor || undefined);
            setCompany(config?.company || undefined);
        } catch (error) {
            console.error('Error loading chat config:', error);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const patchChat = (patch: Partial<FeatureBlock>) => {
        setIntegration((prev) => (prev ? applyChatPatch(prev, patch) : prev));
    };

    const prompts = settings.prompts || { enabled: false };
    const patchPrompts = (patch: Partial<NonNullable<ChatSettings['prompts']>>) =>
        patchSettings({ prompts: { ...prompts, ...patch } });

    const roomPhoto = settings.roomPhoto || { enabled: false };
    const patchRoomPhoto = (patch: Partial<NonNullable<ChatSettings['roomPhoto']>>) =>
        patchSettings({ roomPhoto: { ...roomPhoto, ...patch } });

    const returning = settings.returningNudge || { enabled: false };
    const patchReturning = (patch: Partial<NonNullable<ChatSettings['returningNudge']>>) =>
        patchSettings({ returningNudge: { ...returning, ...patch } });

    // The shop's team in the chat (shopper-handoff in the API): opt-in.
    const handoff = settings.handoff || { enabled: false };
    const patchHandoff = (patch: Partial<NonNullable<ChatSettings['handoff']>>) =>
        patchSettings({ handoff: { ...handoff, ...patch } });

    const patchSettings = (patch: Partial<ChatSettings>) =>
        patchChat({ settings: { ...settings, ...patch } as ChatSettings });

    // Saved a moment after the last change; the page's top bar says so.
    useAutosave({
        data: integration?.features?.chat ?? null,
        ready: !loading && !!integration,
        save: async () => {
            if (!integration) return;
            const next = prepareChatSave(integration);
            await settingsAPI.updateConfig({ integration: next });
            setIntegration(next);
        },
    });

    /** Open one folded section (closing the others), and show the preview it is about. */
    const section = (id: Exclude<ChatSettingsTab, 'general'>, preview?: PreviewView) => ({
        open: settingsTab === id,
        onOpenChange: (open: boolean) => {
            setSettingsTab(open ? id : 'general');
            if (open && preview) setPreviewView(preview);
        },
    });

    if (loading) {
        return (
            <div className="flex min-h-0 flex-1">
                <div className="flex w-full flex-col gap-4 border-e p-5 lg:w-[440px]">
                    <Skeleton className="h-20 w-full" />
                    <Skeleton className="h-32 w-full" />
                    <Skeleton className="h-14 w-full" />
                    <Skeleton className="h-14 w-full" />
                </div>
                <div className="hidden flex-1 bg-muted lg:block" />
            </div>
        );
    }

    const cornerLabel = settings.position === 'left' ? t('CHAT.APPEARANCE.LEFT', 'Left') : t('CHAT.APPEARANCE.RIGHT', 'Right');

    return (
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
            {/* ── Settings ─────────────────────────────────────────────── */}
            <aside className="flex min-h-0 w-full flex-col border-b bg-background lg:w-[440px] lg:shrink-0 lg:border-b-0 lg:border-e">
                <div className="flex min-h-0 flex-1 flex-col gap-7 overflow-y-auto px-5 py-5">
                    {!installed && (
                        <Alert>
                            <Plug className="size-4" />
                            <AlertDescription className="flex flex-wrap items-center gap-2">
                                {t('CHAT.NOT_INSTALLED', 'Add your site and install the script before the chat can appear on your product pages.')}
                                <Button variant="link" className="h-auto p-0" onClick={() => navigate('/home/integration')}>
                                    {t('CHAT.GO_TO_INTEGRATION', 'Open integration settings')}
                                </Button>
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* 1 — Is it on */}
                    <section className="flex flex-col gap-3 rounded-xl border p-4">
                        <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <h3 className="text-sm font-medium">{t('SHOPPER_UI.SETTINGS.STATUS_TITLE')}</h3>
                                <p className="mt-1 text-xs text-muted-foreground">{t('SHOPPER_UI.SETTINGS.STATUS_DESC')}</p>
                            </div>
                            <Switch
                                checked={enabled}
                                onCheckedChange={(on) => patchChat({ enabled: on })}
                                aria-label={t('CHAT.STATUS.TOGGLE', 'Enable shopper chat')}
                                data-testid="chat-enabled-switch"
                            />
                        </div>
                        <StatusBadge tone={enabled && installed ? 'success' : enabled ? 'warning' : 'neutral'} className="text-muted-foreground">
                            {enabled && installed
                                ? t('SHOPPER_UI.SETTINGS.LIVE')
                                : enabled
                                    ? t('SHOPPER_UI.SETTINGS.ON_NOT_INSTALLED')
                                    : t('SHOPPER_UI.SETTINGS.OFF_HINT')}
                        </StatusBadge>
                    </section>

                    {/* 2 — What it says first */}
                    <SettingsGroup title={t('SHOPPER_UI.SETTINGS.GREETING_TITLE')} description={t('SHOPPER_UI.SETTINGS.GREETING_DESC')}>
                        <div>
                            <Textarea
                                id="chat-greeting"
                                rows={3}
                                aria-label={t('CHAT.CONVERSATION.GREETING', 'Opening message')}
                                value={settings.greeting || ''}
                                placeholder={t('CHAT.CONVERSATION.GREETING_PLACEHOLDER', 'Hi — ask me anything about this product, or tell me what you’re looking for.') as string}
                                onChange={(e) => patchSettings({ greeting: e.target.value })}
                                onFocus={() => setPreviewView('chat')}
                            />
                            <SettingsHint>{t('CHAT.CONVERSATION.GREETING_HINT', 'Shown before the shopper says anything. Leave empty to use the default.')}</SettingsHint>
                        </div>
                    </SettingsGroup>

                    {/* 2b — How it speaks for the shop */}
                    <SettingsGroup title={t('SHOPPER_UI.SETTINGS.RULES_TITLE')} description={t('SHOPPER_UI.SETTINGS.RULES_DESC')}>
                        <AssistantRulesField
                            value={settings.rules}
                            onChange={(rules) => patchSettings({ rules })}
                            onFocus={() => setPreviewView('chat')}
                        />
                    </SettingsGroup>

                    {/* 3 — What it knows */}
                    <SettingsGroup title={t('SHOPPER_UI.SETTINGS.KNOWS_TITLE')} description={t('SHOPPER_UI.SETTINGS.KNOWS_DESC')}>
                        <ul className="flex flex-col gap-2.5 text-sm">
                            <li className="flex items-start gap-2.5">
                                <BookOpen className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                                <span>{t('SHOPPER_UI.SETTINGS.KNOWS_CATALOGUE')}</span>
                            </li>
                            <li className="flex items-start gap-2.5">
                                <ShieldCheck className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                                <span>{t('SHOPPER_UI.SETTINGS.KNOWS_NO_GUESS')}</span>
                            </li>
                        </ul>
                        <div className="flex flex-wrap gap-2">
                            <Button variant="outline" size="sm" asChild>
                                <Link to="/products/catalog">{t('SHOPPER_UI.SETTINGS.IMPROVE_PRODUCTS')}</Link>
                            </Button>
                            <Button variant="ghost" size="sm" asChild>
                                <Link to="/assistant/questions"><CircleHelp />{t('SHOPPER_UI.SETTINGS.SEE_UNANSWERED')}</Link>
                            </Button>
                        </div>
                    </SettingsGroup>

                    {/* 3b — What it read on the vendor's own website */}
                    <SettingsGroup title={t('SHOPPER_UI.STORE.TITLE')} description={t('SHOPPER_UI.STORE.DESC')}>
                        <StoreKnowledgePanel api={storeKnowledgeApi} />
                    </SettingsGroup>

                    {/* 4 — Folded: the rest */}
                    <div className="flex flex-col gap-3">
                        <p className="text-xs font-medium text-muted-foreground">{t('SHOPPER_UI.SETTINGS.MORE_OPTIONS')}</p>

                        <Disclosure
                            id="photos"
                            icon={<ImagePlus />}
                            title={t('SHOPPER_UI.SETTINGS.PHOTOS_TITLE')}
                            description={t('SHOPPER_UI.SETTINGS.PHOTOS_DESC')}
                            status={<OnOff on={!!roomPhoto.enabled} />}
                            {...section('photos', 'chat')}
                        >
                            <div className="flex items-center justify-between gap-4">
                                <FieldLabel htmlFor="chat-room-photo-enabled">
                                    {t('CHAT.ROOM_PHOTO.ENABLED', 'Allow photos in the chat')}
                                </FieldLabel>
                                <Switch
                                    id="chat-room-photo-enabled"
                                    checked={!!roomPhoto.enabled}
                                    aria-label={t('CHAT.ROOM_PHOTO.TOGGLE', 'Allow photos in the chat') as string}
                                    onCheckedChange={(v) => { setPreviewView('chat'); patchRoomPhoto({ enabled: v }); }}
                                />
                            </div>
                            {roomPhoto.enabled && (
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <FieldLabel htmlFor="chat-room-photo-ask">
                                            {t('CHAT.ROOM_PHOTO.AGENT_MAY_ASK', 'Let the assistant ask for one')}
                                        </FieldLabel>
                                        <SettingsHint className="mt-0">
                                            {t(
                                                'CHAT.ROOM_PHOTO.AGENT_MAY_ASK_HINT',
                                                'With this off the button is still there, but the assistant never brings it up — it only uses a photo the shopper decided to send.',
                                            )}
                                        </SettingsHint>
                                    </div>
                                    <Switch
                                        id="chat-room-photo-ask"
                                        checked={roomPhoto.agentMayAsk !== false}
                                        aria-label={t('CHAT.ROOM_PHOTO.AGENT_MAY_ASK', 'Let the assistant ask for one') as string}
                                        onCheckedChange={(v) => patchRoomPhoto({ agentMayAsk: v })}
                                    />
                                </div>
                            )}
                        </Disclosure>

                        {/* People from the shop, answering in the chat (shopper-handoff). */}
                        <Disclosure
                            id="team"
                            icon={<Headset />}
                            title={t('SHOPPER_TEAM.SETTINGS_TITLE')}
                            description={t('SHOPPER_TEAM.SETTINGS_DESC')}
                            status={<OnOff on={!!handoff.enabled} />}
                            {...section('team', 'chat')}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <FieldLabel htmlFor="chat-handoff-enabled">{t('SHOPPER_TEAM.SETTINGS_TOGGLE')}</FieldLabel>
                                    <SettingsHint className="mt-0">{t('SHOPPER_TEAM.SETTINGS_HINT')}</SettingsHint>
                                </div>
                                <Switch
                                    id="chat-handoff-enabled"
                                    data-testid="chat-handoff-enabled"
                                    checked={!!handoff.enabled}
                                    aria-label={t('SHOPPER_TEAM.SETTINGS_TOGGLE') as string}
                                    onCheckedChange={(v) => { setPreviewView('chat'); patchHandoff({ enabled: v }); }}
                                />
                            </div>
                            {handoff.enabled && (
                                <>
                                    <div>
                                        <FieldLabel htmlFor="chat-handoff-email">{t('SHOPPER_TEAM.SETTINGS_EMAIL')}</FieldLabel>
                                        <Input
                                            id="chat-handoff-email"
                                            type="email"
                                            value={handoff.alertEmail || ''}
                                            placeholder="equipe@boutique.fr"
                                            onChange={(e) => patchHandoff({ alertEmail: e.target.value.trim() })}
                                        />
                                        <SettingsHint>{t('SHOPPER_TEAM.SETTINGS_EMAIL_HINT')}</SettingsHint>
                                    </div>
                                    <div>
                                        <Button variant="outline" size="sm" asChild>
                                            <Link to="/assistant/conversations?outcome=waiting">{t('SHOPPER_TEAM.SETTINGS_OPEN_INBOX')}</Link>
                                        </Button>
                                    </div>
                                </>
                            )}
                        </Disclosure>

                        <Disclosure
                            id="questions"
                            icon={<ListChecks />}
                            title={t('SHOPPER_UI.SETTINGS.PROMPTS_TITLE')}
                            description={t('SHOPPER_UI.SETTINGS.PROMPTS_DESC')}
                            status={<OnOff on={!!prompts.enabled} />}
                            {...section('questions', 'page')}
                        >
                            <div className="flex items-center justify-between gap-4">
                                <FieldLabel htmlFor="chat-prompts-enabled">
                                    {t('CHAT.PROMPTS.ENABLED', 'Show the questions block')}
                                </FieldLabel>
                                <Switch
                                    id="chat-prompts-enabled"
                                    checked={!!prompts.enabled}
                                    aria-label={t('CHAT.PROMPTS.TOGGLE', 'Show suggested questions') as string}
                                    onCheckedChange={(v) => { setPreviewView('page'); patchPrompts({ enabled: v }); }}
                                />
                            </div>

                            {prompts.enabled && (
                                <>
                                    <div>
                                        <FieldLabel htmlFor="chat-prompts-heading">
                                            {t('CHAT.PROMPTS.HEADING', 'Heading')}
                                        </FieldLabel>
                                        <Input
                                            id="chat-prompts-heading"
                                            value={prompts.heading || ''}
                                            placeholder={t('CHAT.PROMPTS.HEADING_PLACEHOLDER', 'A question about this product?') as string}
                                            onChange={(e) => patchPrompts({ heading: e.target.value })}
                                            onFocus={() => setPreviewView('page')}
                                        />
                                    </div>

                                    <div>
                                        <FieldLabel htmlFor="chat-prompts-cta">
                                            {t('CHAT.PROMPTS.CTA', 'Button')}
                                        </FieldLabel>
                                        <Input
                                            id="chat-prompts-cta"
                                            value={prompts.ctaLabel || ''}
                                            placeholder={t('CHAT.PROMPTS.CTA_PLACEHOLDER', 'Ask my question') as string}
                                            onChange={(e) => patchPrompts({ ctaLabel: e.target.value })}
                                            onFocus={() => setPreviewView('page')}
                                        />
                                    </div>

                                    {/* Placement is a CSS selector: only for whoever set up the site. */}
                                    <Collapsible className="rounded-lg bg-muted/40">
                                        <CollapsibleTrigger className="group/adv flex w-full items-center justify-between gap-3 px-3 py-2.5 text-start text-xs font-medium text-muted-foreground">
                                            {t('SHOPPER_UI.SETTINGS.ADVANCED_PLACEMENT')}
                                            <ChevronDown className="size-3.5 transition-transform group-data-[state=open]/adv:rotate-180" />
                                        </CollapsibleTrigger>
                                        <CollapsibleContent>
                                            <div className="flex flex-col gap-4 px-3 pb-3">
                                                <div>
                                                    <FieldLabel htmlFor="chat-prompts-container">
                                                        {t('CHAT.PROMPTS.CONTAINER', 'Where it goes')}
                                                    </FieldLabel>
                                                    <Input
                                                        id="chat-prompts-container"
                                                        className="bg-background"
                                                        value={prompts.containerSelector || ''}
                                                        placeholder=".product-details"
                                                        onChange={(e) => patchPrompts({ containerSelector: e.target.value })}
                                                    />
                                                    <SettingsHint>
                                                        {t(
                                                            'CHAT.PROMPTS.CONTAINER_HINT',
                                                            'A CSS selector for the element on your product page to place the block against — usually the column holding the product title and Add to Cart.',
                                                        )}
                                                    </SettingsHint>
                                                </div>

                                                <div>
                                                    <FieldLabel htmlFor="chat-prompts-position">
                                                        {t('CHAT.PROMPTS.POSITION', 'Placement')}
                                                    </FieldLabel>
                                                    <select
                                                        id="chat-prompts-position"
                                                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                                                        value={prompts.insertPosition || 'append'}
                                                        onChange={(e) => patchPrompts({ insertPosition: e.target.value as any })}
                                                    >
                                                        <option value="append">{t('CHAT.PROMPTS.APPEND', 'Inside, at the end')}</option>
                                                        <option value="prepend">{t('CHAT.PROMPTS.PREPEND', 'Inside, at the start')}</option>
                                                        <option value="after">{t('CHAT.PROMPTS.AFTER', 'Just after it')}</option>
                                                        <option value="before">{t('CHAT.PROMPTS.BEFORE', 'Just before it')}</option>
                                                    </select>
                                                </div>

                                                <div>
                                                    <FieldLabel htmlFor="chat-prompts-resume">
                                                        {t('CHAT.PROMPTS.RESUME', 'Button, mid-conversation')}
                                                    </FieldLabel>
                                                    <Input
                                                        id="chat-prompts-resume"
                                                        className="bg-background"
                                                        value={prompts.resumeLabel || ''}
                                                        placeholder={t('CHAT.PROMPTS.RESUME_PLACEHOLDER', 'Resume the conversation') as string}
                                                        onChange={(e) => patchPrompts({ resumeLabel: e.target.value })}
                                                    />
                                                    <SettingsHint>{t('CHAT.PROMPTS.RESUME_HINT', 'Shown instead when the shopper already has a conversation open.')}</SettingsHint>
                                                </div>
                                            </div>
                                        </CollapsibleContent>
                                    </Collapsible>
                                </>
                            )}
                        </Disclosure>

                        <Disclosure
                            id="returning"
                            icon={<Repeat />}
                            title={t('SHOPPER_UI.SETTINGS.RETURNING_TITLE')}
                            description={t('SHOPPER_UI.SETTINGS.RETURNING_DESC')}
                            status={<OnOff on={!!returning.enabled} />}
                            {...section('returning', 'returning')}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <FieldLabel htmlFor="chat-returning-enabled">
                                        {t('SHOPPER_UI.SETTINGS.RETURNING_ENABLED')}
                                    </FieldLabel>
                                    <SettingsHint className="mt-0">
                                        {t('SHOPPER_UI.SETTINGS.RETURNING_HOW')}
                                    </SettingsHint>
                                </div>
                                <Switch
                                    id="chat-returning-enabled"
                                    checked={!!returning.enabled}
                                    aria-label={t('SHOPPER_UI.SETTINGS.RETURNING_ENABLED') as string}
                                    data-testid="chat-returning-switch"
                                    onCheckedChange={(v) => { setPreviewView('returning'); patchReturning({ enabled: v }); }}
                                />
                            </div>

                            {returning.enabled && (
                                <>
                                    <div>
                                        <FieldLabel htmlFor="chat-returning-message">
                                            {t('SHOPPER_UI.SETTINGS.RETURNING_MESSAGE')}
                                        </FieldLabel>
                                        <Input
                                            id="chat-returning-message"
                                            maxLength={160}
                                            value={returning.message || ''}
                                            placeholder={t('SHOPPER_UI.SETTINGS.RETURNING_MESSAGE_PLACEHOLDER') as string}
                                            onChange={(e) => patchReturning({ message: e.target.value })}
                                            onFocus={() => setPreviewView('returning')}
                                        />
                                        <SettingsHint>{t('SHOPPER_UI.SETTINGS.RETURNING_MESSAGE_HINT')}</SettingsHint>
                                    </div>

                                    <ReturningRuleField
                                        value={returning}
                                        onChange={(rule) => patchReturning(rule)}
                                    />

                                    <ReturningConsentField
                                        mode={returning.consentMode}
                                        onChange={(consentMode) => patchReturning({ consentMode })}
                                    />
                                </>
                            )}
                        </Disclosure>

                        <Disclosure
                            id="appearance"
                            icon={<Palette />}
                            title={t('SHOPPER_UI.SETTINGS.LOOK_TITLE')}
                            description={t('SHOPPER_UI.SETTINGS.LOOK_DESC')}
                            status={(
                                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <span className="size-3 rounded-full border" style={{ background: launcherColors.background }} aria-hidden />
                                    {cornerLabel}
                                </span>
                            )}
                            {...section('appearance', 'chat')}
                        >
                            <div>
                                <FieldLabel htmlFor="chat-position">{t('CHAT.APPEARANCE.POSITION', 'Corner')}</FieldLabel>
                                <ToggleGroup
                                    type="single"
                                    variant="outline"
                                    size="sm"
                                    id="chat-position"
                                    value={settings.position}
                                    onValueChange={(v) => v && patchSettings({ position: v as 'left' | 'right' })}
                                >
                                    <ToggleGroupItem value="left">{t('CHAT.APPEARANCE.LEFT', 'Left')}</ToggleGroupItem>
                                    <ToggleGroupItem value="right">{t('CHAT.APPEARANCE.RIGHT', 'Right')}</ToggleGroupItem>
                                </ToggleGroup>
                            </div>

                            <div>
                                <FieldLabel htmlFor="chat-label">{t('CHAT.APPEARANCE.LABEL', 'Button label')}</FieldLabel>
                                <Input
                                    id="chat-label"
                                    value={settings.label || ''}
                                    placeholder={t('CHAT.APPEARANCE.LABEL_PLACEHOLDER', 'Shopping Assistant') as string}
                                    onChange={(e) => patchSettings({ label: e.target.value })}
                                />
                                <SettingsHint>{t('CHAT.APPEARANCE.LABEL_HINT', 'Shown on the launcher next to its icon. Leave empty to use “Shopping Assistant” in your site’s language.')}</SettingsHint>
                            </div>

                            <div className="flex flex-col gap-4">
                                <div>
                                    <p className="text-xs font-medium text-muted-foreground">{t('CHAT.APPEARANCE.COLORS', 'Launcher colours')}</p>
                                    <SettingsHint className="mt-1">
                                        {t('CHAT.APPEARANCE.COLORS_HINT', 'Used by the launcher button and the questions shown above it. Leave a colour on Auto to follow your brand.')}
                                    </SettingsHint>
                                </div>
                                <SettingsColorField
                                    id="chat-color"
                                    label={t('CHAT.APPEARANCE.BACKGROUND', 'Background')}
                                    value={settings.color}
                                    auto={launcherColors.background}
                                    autoLabel={t('CHAT.APPEARANCE.USE_BRAND', 'Use brand colour')}
                                    onChange={(color) => { setPreviewHover(false); patchSettings({ color }); }}
                                />
                                <SettingsColorField
                                    id="chat-text-color"
                                    label={t('CHAT.APPEARANCE.TEXT', 'Text')}
                                    value={settings.textColor}
                                    auto={launcherColors.text}
                                    autoLabel={t('CHAT.APPEARANCE.AUTO', 'Auto')}
                                    onChange={(textColor) => { setPreviewHover(false); patchSettings({ textColor }); }}
                                />
                                <SettingsColorField
                                    id="chat-hover-color"
                                    label={t('CHAT.APPEARANCE.HOVER_BACKGROUND', 'Background on hover')}
                                    value={settings.hoverColor}
                                    auto={launcherColors.hoverBackground}
                                    autoLabel={brandSecondaryColor
                                        ? t('CHAT.APPEARANCE.USE_SECONDARY', 'Use secondary colour')
                                        : t('CHAT.APPEARANCE.AUTO', 'Auto')}
                                    onChange={(hoverColor) => { setPreviewHover(true); patchSettings({ hoverColor }); }}
                                />
                                <SettingsColorField
                                    id="chat-hover-text-color"
                                    label={t('CHAT.APPEARANCE.HOVER_TEXT', 'Text on hover')}
                                    value={settings.hoverTextColor}
                                    auto={launcherColors.hoverText}
                                    autoLabel={t('CHAT.APPEARANCE.AUTO', 'Auto')}
                                    onChange={(hoverTextColor) => { setPreviewHover(true); patchSettings({ hoverTextColor }); }}
                                />
                            </div>
                        </Disclosure>
                    </div>
                </div>
            </aside>

            {/* ── Preview canvas ───────────────────────────────────────── */}
            <div className="relative min-h-[560px] flex-1 overflow-auto bg-muted lg:min-h-[640px]">
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 opacity-55"
                    style={{
                        backgroundImage: 'radial-gradient(circle, var(--muted-foreground) 1px, transparent 1px)',
                        backgroundSize: '20px 20px',
                    }}
                />

                <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                    <ToggleGroup
                        type="single"
                        variant="outline"
                        size="sm"
                        className="bg-background"
                        value={previewView}
                        onValueChange={(v) => v && setPreviewView(v as PreviewView)}
                    >
                        <ToggleGroupItem value="chat">{t('CHAT.PREVIEW.VIEW_CHAT', 'Chat window')}</ToggleGroupItem>
                        <ToggleGroupItem value="page">{t('CHAT.PREVIEW.VIEW_PAGE', 'Product page')}</ToggleGroupItem>
                        {returning.enabled && (
                            <ToggleGroupItem value="returning">{t('SHOPPER_UI.SETTINGS.RETURNING_TITLE')}</ToggleGroupItem>
                        )}
                    </ToggleGroup>

                    <ToggleGroup
                        type="single"
                        variant="outline"
                        size="sm"
                        className={cn('bg-background', settingsTab !== 'appearance' && 'hidden sm:flex')}
                        value={previewHover ? 'hover' : 'rest'}
                        onValueChange={(v) => v && setPreviewHover(v === 'hover')}
                    >
                        <ToggleGroupItem value="rest">{t('CHAT.PREVIEW.RESTING', 'Resting')}</ToggleGroupItem>
                        <ToggleGroupItem value="hover"><MousePointerClick className="size-3.5" />{t('CHAT.PREVIEW.HOVER', 'Hover')}</ToggleGroupItem>
                    </ToggleGroup>
                </div>

                <div className="relative flex min-h-[calc(100%-60px)] items-center justify-center px-6 pb-10 pt-2">
                    <ChatPreview
                        settings={settings}
                        brandColor={brandColor}
                        brandSecondaryColor={brandSecondaryColor}
                        company={company}
                        view={previewView}
                        hovered={previewHover}
                    />
                </div>
            </div>
        </div>
    );
};

export const ChatSettingsPage = () => {
    const { t } = useTranslation();
    const autosave = useAutosaveReporterState();

    return (
        <div className="flex flex-col" style={{ height: SHELL_HEIGHT }}>
            <TopNavBar
                title={t('CHAT.TITLE', 'Shopper chat')}
                breadcrumbs={[
                    { label: t('SIDEBAR.SHOPPING_ASSISTANT.TITLE', 'Shopping Assistant') },
                    { label: t('SIDEBAR.SHOPPING_ASSISTANT.SETTINGS', 'Settings') },
                ]}
                status={<AutosaveIndicator status={autosave.status} onRetry={autosave.retry} />}
            />
            <FeatureGate feature="shopperChat">
                <AutosaveReporterContext.Provider value={autosave.reporter}>
                    <ChatSettingsBody />
                </AutosaveReporterContext.Provider>
            </FeatureGate>
        </div>
    );
};

export default ChatSettingsPage;
